/**
 * A dependency key for hooks that take a caller-supplied dependency array.
 *
 * `useEffect(fn, deps)` with a non-literal `deps` needs an eslint-disable,
 * and any disabled react-hooks rule makes the React Compiler skip the whole
 * hook. Keying the effect on `depsIdentityKey(deps)` instead re-runs it on
 * exactly the renders the array would have: primitives compare by value,
 * objects and functions by identity (a new-but-equal object is a change, as
 * it is for React's `Object.is` comparison).
 */

const ids = new WeakMap<object, number>();
let nextId = 0;

/** Stable id for an object, assigned on first sight (idempotent, so render-safe). */
function idOf(obj: object): number {
  let id = ids.get(obj);
  if (id === undefined) {
    id = ++nextId;
    ids.set(obj, id);
  }
  return id;
}

/** A string that changes exactly when some element of `deps` changes identity. */
export function depsIdentityKey(deps: readonly unknown[]): string {
  return deps
    .map((dep) => {
      if (dep === null) return 'null';
      const type = typeof dep;
      if (type === 'object' || type === 'function') return `ref:${idOf(dep as object)}`;
      // Object.is semantics: -0 and NaN keep their own keys.
      if (type === 'number' && Object.is(dep, -0)) return 'number:-0';
      return `${type}:${String(dep)}`;
    })
    .join('\u0001');
}
